// src/services/authService.ts
// All Firebase auth + Firestore side-effects. No React, no hooks.
// Components call these functions; useFirebaseAuth.ts owns the listener.

import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  linkWithCredential,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';

import { app, auth, db, storage } from '../lib/firebase';

// Firebase callable functions — region must match functions/src/index.ts
const _functions = getFunctions(app, 'europe-central2');
import { progressSync } from './progressSync';
import { useAuthStore, type UserProfile } from '../stores/useAuthStore';

// ── Error helpers ────────────────────────────────────────────────────────────

const AUTH_ERROR_MAP: Record<string, string> = {
  'auth/email-already-in-use': 'This email is already registered. Please try logging in.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Password should be at least 6 characters long.',
  'auth/user-not-found': 'No account found with this email. Please sign up first.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Incorrect email or password. Please try again.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
};

export function getAuthErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    return AUTH_ERROR_MAP[code] ?? 'An error occurred. Please try again.';
  }
  return 'An error occurred. Please try again.';
}

// ── Anonymous auth ───────────────────────────────────────────────────────────

let _signingInAnonymously = false;

/**
 * Signs in anonymously so Firestore security rules (which require auth) let
 * the user read the books collection without a registered account.
 * Call this after logout and on initial load when no user is present.
 */
export async function ensureAnonymousAuth(): Promise<void> {
  if (_signingInAnonymously) return;
  _signingInAnonymously = true;
  try {
    await signInAnonymously(auth);
  } catch (e) {
    console.warn('⚠️ Anonymous sign-in failed (books may not load):', e);
  } finally {
    _signingInAnonymously = false;
  }
}

// ── Welcome email ─────────────────────────────────────────────────────────────

/**
 * Fire-and-forget — never throws to the caller.
 * Calls the Firebase sendWelcomeEmail callable function.
 * The callable SDK attaches the user's ID token automatically.
 */
export async function sendWelcomeEmail(_user: User): Promise<void> {
  try {
    const sendWelcomeEmailFn = httpsCallable<
      Record<string, never>,
      { sent: boolean; reason?: string }
    >(_functions, 'sendWelcomeEmail');

    const result = await sendWelcomeEmailFn({});
    if (result.data.sent) {
      console.log('✅ Welcome email sent');
    } else {
      console.warn('⚠️ Welcome email not sent:', result.data.reason);
    }
  } catch (err) {
    console.warn('⚠️ Welcome email error (non-fatal):', err);
  }
}

// ── Firestore profile ────────────────────────────────────────────────────────

/**
 * Reads the /users/{uid} document and returns a typed UserProfile.
 * Returns null when the document doesn't exist or a read error occurs.
 */
export async function loadUserProfile(uid: string): Promise<UserProfile | null> {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return null;
    const d = snap.data();
    const sub = d['subscription'] ?? {};
    return {
      displayName: d['displayName'] ?? '',
      email: d['email'] ?? '',
      phone: d['phone'] ?? '',
      occupation: d['occupation'] ?? '',
      institution: d['institution'] ?? '',
      avatarUrl: d['avatarUrl'] ?? null,
      avatarStoragePath: d['avatarStoragePath'] ?? null,
      authProvider: d['authProvider'] ?? null,
      hasFullAccess: d['hasFullAccess'] === true,
      subscription: {
        status: sub['status'] ?? 'inactive',
        plan: sub['plan'] ?? null,
        startDate: sub['startDate'] ? sub['startDate'].toDate() : null,
        endDate: sub['endDate'] ? sub['endDate'].toDate() : null,
        isLifetime: sub['isLifetime'] === true,
        promoCode: sub['promoCode'] ?? undefined,
      },
      createdAt: d['createdAt'] ?? '',
    };
  } catch (e) {
    console.error('Error loading user profile:', e);
    return null;
  }
}

// ── Google credential linking ─────────────────────────────────────────────────

/** Stored when Google sign-in fails with account-exists-with-different-credential. */
let _pendingGoogleCredential: ReturnType<typeof GoogleAuthProvider.credentialFromError> | null =
  null;

export function clearPendingGoogleCredential(): void {
  _pendingGoogleCredential = null;
}

// ── Login ────────────────────────────────────────────────────────────────────

export async function handleLogin(email: string, password: string): Promise<void> {
  const credential = await signInWithEmailAndPassword(auth, email, password);

  // Complete account linking if a Google credential is pending (flow from
  // auth/account-exists-with-different-credential error path).
  if (_pendingGoogleCredential) {
    try {
      await linkWithCredential(credential.user, _pendingGoogleCredential);
      console.log('✅ Google credential linked to email/password account');
    } catch (linkErr) {
      console.warn('Account linking failed (non-fatal):', linkErr);
    } finally {
      _pendingGoogleCredential = null;
    }
  }

  // Migrate anonymous IDB progress → user, then flush queue.
  progressSync.migrateOnLogin(credential.user).catch(() => {});
  progressSync.syncPendingProgress().catch(() => {});
}

// ── Signup ───────────────────────────────────────────────────────────────────

export async function handleSignup(name: string, email: string, password: string): Promise<void> {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(credential.user, { displayName: name });
  await setDoc(doc(db, 'users', credential.user.uid), {
    displayName: name,
    email,
    phone: '',
    occupation: '',
    institution: '',
    subscription: { status: 'inactive', plan: null, endDate: null },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  await progressSync.migrateOnLogin(credential.user).catch(() => {});
  await progressSync.syncPendingProgress().catch(() => {});
  sendWelcomeEmail(credential.user).catch(() => {}); // fire-and-forget
}

// ── Google Sign-In ────────────────────────────────────────────────────────────

export interface GoogleSignInResult {
  isNewUser: boolean;
  displayName: string;
}

export async function handleGoogleSignIn(): Promise<GoogleSignInResult> {
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  const user = result.user;
  const additionalInfo = getAdditionalUserInfo(result);
  const isNewUser = additionalInfo?.isNewUser ?? false;

  // Create or update the Firestore user document.
  try {
    const existingDoc = await getDoc(doc(db, 'users', user.uid));
    if (!existingDoc.exists()) {
      await setDoc(doc(db, 'users', user.uid), {
        displayName: user.displayName ?? '',
        email: user.email ?? '',
        phone: '',
        occupation: '',
        institution: '',
        subscription: { status: 'inactive', plan: null, endDate: null },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        authProvider: 'google',
      });
    } else {
      const d = existingDoc.data();
      // Restore custom displayName / avatar that may have been set after initial
      // Google sign-in (Google sign-in overwrites photoURL with Google's CDN url).
      const restoredName = d['displayName'] || user.displayName || '';
      const restoredPhoto = d['avatarUrl'] || null;
      try {
        await updateProfile(user, { displayName: restoredName, photoURL: restoredPhoto });
      } catch (_) {}
      await setDoc(
        doc(db, 'users', user.uid),
        { authProvider: 'google', updatedAt: new Date().toISOString() },
        { merge: true }
      );
    }
  } catch (docErr) {
    console.warn('Could not update Firestore profile for Google user:', docErr);
  }

  progressSync.migrateOnLogin(user).catch(() => {});
  progressSync.syncPendingProgress().catch(() => {});
  if (isNewUser) sendWelcomeEmail(user).catch(() => {});

  return { isNewUser, displayName: user.displayName ?? '' };
}

/** Returns info that lets the UI handle specific error codes gracefully. */
export interface GoogleErrorResult {
  /** true = error was handled; component should not show generic error message */
  handled: boolean;
  /** Present when the error was auth/account-exists-with-different-credential */
  pendingEmail?: string;
  /** Human-readable message for non-handled errors */
  message?: string;
}

export function handleGoogleError(error: unknown): GoogleErrorResult {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? (error as { code: string }).code
      : '';

  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return { handled: true };
  }

  if (code === 'auth/popup-blocked') {
    return {
      handled: false,
      message: 'Popup was blocked by your browser. Please allow popups for this site and try again.',
    };
  }

  if (code === 'auth/account-exists-with-different-credential') {
    _pendingGoogleCredential = GoogleAuthProvider.credentialFromError(
      error as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]
    );
    const pendingEmail = (error as { customData?: { email?: string } }).customData?.email;
    return { handled: true, pendingEmail };
  }

  return { handled: false, message: 'Google sign-in failed. Please try again.' };
}

// ── Logout ───────────────────────────────────────────────────────────────────

/**
 * Safe logout: disables real-time listeners, flushes pending sync (with a
 * 5-second timeout), signs out, then immediately signs in anonymously so
 * Firestore rules keep allowing book reads.
 *
 * Throws if exercise is active — caller should display the error.
 */
export async function handleLogout(): Promise<void> {
  const store = useAuthStore.getState();

  if (store.isExerciseActive) {
    throw new Error('Please complete your current exercise before logging out.');
  }
  if (store.isLoggingOut) return;

  store.setIsLoggingOut(true);

  try {
    // Disable real-time listeners BEFORE signOut to avoid permission errors.
    progressSync.disableRealTimeListeners();

    // Best-effort sync with a hard timeout.
    if (store.user && store.syncStatus.pendingItems > 0) {
      try {
        await Promise.race([
          progressSync.syncPendingProgress(),
          new Promise<never>((_, r) =>
            setTimeout(() => r(new Error('Sync timeout')), 5_000)
          ),
        ]);
      } catch (e) {
        console.warn('⚠️ Could not sync before logout:', e);
      }
    }

    // Brief pause so in-flight Firestore writes settle.
    await new Promise<void>((resolve) => setTimeout(resolve, 300));

    clearProgressLocalStorage();
    await signOut(auth);

    store.setProfile(null);
    store.setSyncStatus({ isSyncing: false, lastSync: null, pendingItems: 0 });

    navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_AUDIO_CACHE' });
    console.log('✅ Logout completed safely');
  } finally {
    store.setIsLoggingOut(false);
    // Always restore anonymous auth so Firestore rules keep working.
    ensureAnonymousAuth();
  }
}

// ── Profile update ────────────────────────────────────────────────────────────

export interface ProfileUpdateData {
  displayName: string;
  phone: string;
  institution: string;
  occupation: string;
  avatarFile: File | null;
  avatarRemoved: boolean;
}

export async function handleProfileUpdate(data: ProfileUpdateData): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('No user signed in');

  // Text fields — always save.
  await updateProfile(user, { displayName: data.displayName });
  await setDoc(
    doc(db, 'users', user.uid),
    {
      displayName: data.displayName,
      phone: data.phone,
      institution: data.institution,
      occupation: data.occupation,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  // Avatar upload.
  if (data.avatarFile) {
    // Delete previous avatar from Storage if exists.
    try {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const oldPath = snap.data()?.['avatarStoragePath'];
      if (oldPath) await deleteObject(ref(storage, oldPath)).catch(() => {});
    } catch (_) {}

    const ts = Date.now();
    const safeName = data.avatarFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `avatars/${user.uid}/${ts}_${safeName}`;
    const snap2 = await uploadBytes(ref(storage, path), data.avatarFile);
    const avatarUrl = await getDownloadURL(snap2.ref);

    await updateProfile(user, { photoURL: avatarUrl });
    await setDoc(doc(db, 'users', user.uid), { avatarUrl, avatarStoragePath: path }, { merge: true });
  } else if (data.avatarRemoved) {
    try {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const oldPath = snap.data()?.['avatarStoragePath'];
      if (oldPath) await deleteObject(ref(storage, oldPath)).catch(() => {});
    } catch (_) {}
    await updateProfile(user, { photoURL: null });
    await setDoc(
      doc(db, 'users', user.uid),
      { avatarUrl: null, avatarStoragePath: null },
      { merge: true }
    );
  }

  // Refresh profile in the store.
  const profile = await loadUserProfile(user.uid);
  useAuthStore.getState().setProfile(profile);
}

// ── Delete account ────────────────────────────────────────────────────────────

export async function deleteUserAccount(): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('No user is logged in.');
  const uid = user.uid;

  // Best-effort flush.
  try {
    await Promise.race([
      progressSync.flushQueue(),
      new Promise<never>((_, r) => setTimeout(() => r(new Error('timeout')), 5_000)),
    ]);
  } catch (_) {}

  // Delete all sub-collections.
  for (const sub of ['progress', 'subscription', 'queue', 'settings']) {
    try {
      const snap = await getDocs(collection(db, 'users', uid, sub));
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    } catch (_) {}
  }

  // Delete avatar from Storage.
  try {
    const uSnap = await getDoc(doc(db, 'users', uid));
    const p = uSnap.data()?.['avatarStoragePath'];
    if (p) await deleteObject(ref(storage, p));
  } catch (_) {}

  // Delete Firestore doc + Firebase Auth user (order matters — Auth last so
  // if Firestore fails the user can retry).
  await deleteDoc(doc(db, 'users', uid));
  await deleteUser(user);

  progressSync.disableRealTimeListeners();
  clearProgressLocalStorage();
}

// ── Password reset ────────────────────────────────────────────────────────────

export async function handlePasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

// ── Local storage cleanup ─────────────────────────────────────────────────────

export function clearProgressLocalStorage(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (
        k === 'exerciseProgress' ||
        k === 'unitProgress' ||
        k === 'testProgress' ||
        (k != null && k.startsWith('progress:'))
      ) {
        keys.push(k);
      }
    }
    keys.forEach((k) => localStorage.removeItem(k));
    if (keys.length) console.log(`🧹 Cleared ${keys.length} localStorage progress key(s)`);
  } catch (e) {
    console.warn('Could not clear localStorage:', e);
  }
}